/*!
 * EcoBot Killer 偵測
 * 顯示可疑玩家名單、集團分析、個人明細，並支援預覽掃描上傳
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card, CardHeader, CardBody, CardTitle,
  Row, Col, Table, Alert, Badge,
  TabContent, TabPane,
  Input, Button, FormGroup, Progress, Collapse,
  Pagination, PaginationItem, PaginationLink,
  Modal, ModalHeader, ModalBody,
} from "reactstrap";
import axios from "axios";
import { Bar } from "react-chartjs-2";
import TinyPageHeader from "components/tiny/TinyPageHeader";
import TinyLoading, { TinyBusy } from "components/tiny/TinyLoading";
import TinyStatStrip from "components/tiny/TinyStatStrip";
import TinyPageTabs from "components/tiny/TinyPageTabs";
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement,
  Title, Tooltip, Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const API_BASE = "/api/tiny";

const fmt = (n) => {
  if (n === null || n === undefined) return "0";
  return Number(n).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const pct = (n) => {
  if (n === null || n === undefined) return "0%";
  return `${Number(n).toFixed(1)}%`;
};

// v2.24.0 新欄位（eco_net / eco_dep）在「已 deploy 但尚未重新掃描」的空窗期不存在。
// 這種情況顯示「—」而不是 0，避免誤讀成「真的沒從 EcoBot 拿到錢」。
const fmtNew = (n) => (n === null || n === undefined ? "—" : fmt(n));
const pctNew = (n) => (n === null || n === undefined ? "—" : pct(n));
const isLegacy = (s) => s && s.eco_net === undefined;

const riskBadge = (risk) => {
  const colors = { HIGH: "danger", MEDIUM: "warning", LOW: "info" };
  return <Badge color={colors[risk] || "secondary"} style={{ fontSize: "0.85em", padding: "4px 10px" }}>{risk}</Badge>;
};

const DISPOSITION_OPTIONS = [
  { value: '', label: '未設置' },
  { value: 'cleared', label: '確認非可疑', color: '#00d6b4' },
  { value: 'high_watch', label: '高度可疑觀察中', color: '#ff4444' },
  { value: 'low_watch', label: '輕度可疑日後觀察', color: '#ffa500' },
  { value: 'removed', label: '已剔除聯盟', color: '#9A9A9A' },
];
const DISPOSITION_MAP = Object.fromEntries(DISPOSITION_OPTIONS.map(d => [d.value, d]));

const dispositionBadge = (val) => {
  const opt = DISPOSITION_MAP[val || ''];
  if (!opt || !val) return <span style={{ color: '#666', fontSize: '0.82em' }}>—</span>;
  return (
    <Badge style={{
      background: opt.color + '22', color: opt.color,
      border: `1px solid ${opt.color}44`, fontSize: '0.8em', padding: '3px 8px',
    }}>{opt.label}</Badge>
  );
};

const toUTC8 = (isoStr) => {
  if (!isoStr) return "";
  const d = new Date(isoStr + (isoStr.endsWith("Z") ? "" : "Z"));
  const offset = d.getTime() + 8 * 3600 * 1000;
  const d8 = new Date(offset);
  return d8.toISOString().replace('T', ' ').slice(0, 19);
};

const PREVIEW_STATUSES = ['uploading', 'importing', 'scanning', 'cleaning'];
const PREVIEW_STEPS = {
  uploading: { label: '準備中...', pct: 10 },
  importing: { label: '正在匯入 Excel 資料...', pct: 30 },
  scanning: { label: '正在分析可疑玩家...', pct: 70 },
  cleaning: { label: '正在清理暫存資料...', pct: 90 },
  completed: { label: '掃描完成', pct: 100 },
  failed: { label: '掃描失敗', pct: 100 },
};

const gameTypeColor = (gt) => {
  if (!gt) return '#888';
  const g = gt.toUpperCase();
  if (g === 'NLH') return '#1f8ef1';
  if (g === 'AFH' || g === 'AFO') return '#ffa500';
  if (g.startsWith('PLO')) return '#00d6b4';
  return '#bb6bd9';
};

const inputStyle = {
  background: "#27293d",
  color: "#fff",
  border: "1px solid rgba(255,255,255,0.1)",
};

const EXPLOIT_TABS = [
  { key: "risk-list", label: "風險名單", icon: "fa fa-list" },
  { key: "groups", label: "集團分析", icon: "fa fa-users" },
  { key: "detail", label: "個人明細", icon: "fa fa-user" },
];

function TinyExploitDetection() {
  const [activeTab, setActiveTab] = useState("risk-list");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [memoText, setMemoText] = useState("");
  const [memoSaving, setMemoSaving] = useState(false);
  const [dispositionValue, setDispositionValue] = useState("");
  const [dispSaving, setDispSaving] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  // 每週趨勢圖口徑：'pre' = 水前輸贏(扣JP)（預設）；'net' = 帳面 P/L（Win，已扣水）
  const [trendMode, setTrendMode] = useState('pre');

  // Risk list: sort, filter, pagination
  const [sortField, setSortField] = useState("score_total");
  const [sortDir, setSortDir] = useState("desc");
  const [filterRisk, setFilterRisk] = useState([]);
  const [filterDisposition, setFilterDisposition] = useState([]);
  const [filterGameType, setFilterGameType] = useState([]);
  const [filterStake, setFilterStake] = useState([]);
  const [pageData, setPageData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  const [suspectsLoading, setSuspectsLoading] = useState(false);

  // Preview scan state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewWeek, setPreviewWeek] = useState("");
  const [previewFiles, setPreviewFiles] = useState([]);
  const [previewUploading, setPreviewUploading] = useState(false);
  const [previewJob, setPreviewJob] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const pollingRef = useRef(null);
  const fileInputRef = useRef(null);

  // ── Data loading ──

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/exploit/latest`);
      if (res.data.code === 1 && res.data.data) {
        setData(res.data.data);
      } else {
        setData(null);
      }
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSuspects = useCallback(async (page, sField, sDir, riskF, dispF, gtF, skF) => {
    try {
      setSuspectsLoading(true);
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('page_size', pageSize);
      params.set('sort_field', sField);
      params.set('sort_dir', sDir);
      if (riskF.length > 0) params.set('risk', riskF.join(','));
      if (dispF.length > 0) params.set('disposition', dispF.join(','));
      if (gtF.length > 0) params.set('game_type', gtF.join(','));
      if (skF.length > 0) params.set('stake', skF.join(','));
      const res = await axios.get(`${API_BASE}/exploit/suspects?${params}`);
      if (res.data.code === 1) {
        setPageData(res.data.data);
      }
    } catch (_) {}
    finally { setSuspectsLoading(false); }
  }, [pageSize]);

  // ── Preview scan polling ──

  const checkPreviewStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/exploit/preview-status`);
      const job = res.data?.data;
      if (!job) return;

      setPreviewJob(job);

      if (PREVIEW_STATUSES.includes(job.status)) {
        if (!pollingRef.current) {
          pollingRef.current = setInterval(async () => {
            try {
              const r = await axios.get(`${API_BASE}/exploit/preview-status`);
              const j = r.data?.data;
              if (j) setPreviewJob(j);
              if (j && !PREVIEW_STATUSES.includes(j.status)) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
                if (j.status === 'completed') {
                  fetchData();
                  fetchSuspects(1, 'score_total', 'desc', [], [], [], []);
                }
              }
            } catch (_) {}
          }, 2000);
        }
      } else if (job.status === 'completed') {
        fetchData();
      }
    } catch (_) {}
  }, [fetchData]);

  useEffect(() => {
    fetchData();
    fetchSuspects(1, sortField, sortDir, filterRisk, filterDisposition, filterGameType, filterStake);
    checkPreviewStatus();
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Preview scan upload ──

  const handlePreviewScan = async () => {
    if (!previewWeek || previewFiles.length === 0) {
      setPreviewError("請輸入日期範圍並選擇至少一個 Excel 檔案");
      return;
    }
    if (!/^\d{8}-\d{8}$/.test(previewWeek)) {
      setPreviewError("日期格式錯誤，需為 YYYYMMDD-YYYYMMDD（例：20260223-20260227）");
      return;
    }

    setPreviewError("");
    setPreviewUploading(true);

    try {
      const formData = new FormData();
      formData.append("week", previewWeek);
      for (let i = 0; i < previewFiles.length; i++) {
        formData.append("files", previewFiles[i]);
      }

      const res = await axios.post(`${API_BASE}/exploit/preview-scan`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.code !== 1) {
        setPreviewError(res.data.msg || "啟動失敗");
        setPreviewUploading(false);
        return;
      }

      setPreviewFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      checkPreviewStatus();
    } catch (e) {
      setPreviewError(e.response?.data?.msg || e.message);
    } finally {
      setPreviewUploading(false);
    }
  };

  // ── Handlers ──

  const handleSelectPlayer = async (suspect) => {
    setSelectedPlayer(suspect);
    setMemoText(suspect.memo || "");
    setDispositionValue(suspect.disposition || "");
    setActiveTab("detail");
    // 集團分析 Tab 拿到的是精簡版（無 game_stake_breakdown），補抓完整資料
    if (!suspect.game_stake_breakdown) {
      try {
        const res = await axios.get(`${API_BASE}/exploit/suspect/${encodeURIComponent(suspect.pid)}`);
        if (res.data.code === 1 && res.data.data) {
          setSelectedPlayer(prev => (prev && prev.pid === suspect.pid ? res.data.data : prev));
        }
      } catch (_) { /* 補抓失敗就沿用精簡資料 */ }
    }
  };

  const handleSaveMemo = async () => {
    if (!selectedPlayer) return;
    setMemoSaving(true);
    try {
      await axios.post(`${API_BASE}/exploit/memo`, {
        player_id: selectedPlayer.pid,
        memo: memoText,
      });
      setSelectedPlayer(prev => prev ? { ...prev, memo: memoText } : prev);
      setPageData(prev => {
        if (!prev) return prev;
        return { ...prev, suspects: prev.suspects.map(s =>
          s.pid === selectedPlayer.pid ? { ...s, memo: memoText } : s
        )};
      });
    } catch (e) {
      alert("備註儲存失敗: " + e.message);
    } finally {
      setMemoSaving(false);
    }
  };

  const handleSaveDisposition = async (newDisp) => {
    if (!selectedPlayer) return;
    setDispSaving(true);
    try {
      await axios.post(`${API_BASE}/exploit/disposition`, {
        player_id: selectedPlayer.pid,
        disposition: newDisp,
      });
      setDispositionValue(newDisp);
      setSelectedPlayer(prev => prev ? { ...prev, disposition: newDisp } : prev);
      setPageData(prev => {
        if (!prev) return prev;
        return { ...prev, suspects: prev.suspects.map(s =>
          s.pid === selectedPlayer.pid ? { ...s, disposition: newDisp } : s
        )};
      });
    } catch (e) {
      alert("處置方式儲存失敗: " + e.message);
    } finally {
      setDispSaving(false);
    }
  };

  const refetchSuspects = (overrides = {}) => {
    const p = overrides.page ?? 1;
    const sf = overrides.sortField ?? sortField;
    const sd = overrides.sortDir ?? sortDir;
    const rf = overrides.filterRisk ?? filterRisk;
    const df = overrides.filterDisposition ?? filterDisposition;
    const gf = overrides.filterGameType ?? filterGameType;
    const skf = overrides.filterStake ?? filterStake;
    setCurrentPage(p);
    fetchSuspects(p, sf, sd, rf, df, gf, skf);
  };

  const handleSort = (field) => {
    let newDir = 'desc';
    if (field === sortField) {
      newDir = sortDir === 'desc' ? 'asc' : 'desc';
    }
    setSortField(field);
    setSortDir(newDir);
    refetchSuspects({ sortField: field, sortDir: newDir });
  };

  const handlePageChange = (page) => {
    refetchSuspects({ page });
  };

  const toggleRiskFilter = (risk) => {
    const next = filterRisk.includes(risk)
      ? filterRisk.filter(r => r !== risk)
      : [...filterRisk, risk];
    setFilterRisk(next);
    refetchSuspects({ filterRisk: next });
  };

  const toggleDispositionFilter = (disp) => {
    const next = filterDisposition.includes(disp)
      ? filterDisposition.filter(d => d !== disp)
      : [...filterDisposition, disp];
    setFilterDisposition(next);
    refetchSuspects({ filterDisposition: next });
  };

  const toggleGameTypeFilter = (gt) => {
    const next = filterGameType.includes(gt)
      ? filterGameType.filter(g => g !== gt)
      : [...filterGameType, gt];
    setFilterGameType(next);
    refetchSuspects({ filterGameType: next });
  };

  const toggleStakeFilter = (bb) => {
    const next = filterStake.includes(bb)
      ? filterStake.filter(s => s !== bb)
      : [...filterStake, bb];
    setFilterStake(next);
    refetchSuspects({ filterStake: next });
  };

  // ── Preview scan UI helpers ──

  const isPreviewRunning = previewJob && PREVIEW_STATUSES.includes(previewJob.status);
  const previewStepInfo = previewJob ? (PREVIEW_STEPS[previewJob.status] || {}) : {};
  const isPreviewScan = data?.scan_type === "preview";
  const showPreviewJobMsg = isPreviewScan && !isPreviewRunning;

  const paged = pageData || { suspects: [], total: 0, page: 1, page_size: pageSize, total_pages: 0 };

  const SortHeader = ({ field, children, className }) => {
    const active = sortField === field;
    return (
      <th
        className={className}
        onClick={() => handleSort(field)}
        style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      >
        {children}
        <span style={{ marginLeft: 4, color: active ? '#1f8ef1' : '#555', fontSize: '0.78em' }}>
          {active ? (sortDir === 'desc' ? '▼' : '▲') : '⇅'}
        </span>
      </th>
    );
  };

  // ── Render ──

  if (loading && !data) {
    return (
      <div className="content">
        <TinyLoading text="載入偵測結果" hint="可疑玩家名單與集團分析一起載入" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="content">
        <Alert color="danger">載入失敗: {error}</Alert>
      </div>
    );
  }

  const suspects = data?.suspects || [];
  const groups = data?.groups || [];
  const trends = data?.weekly_trends || {};
  const trendsPre = data?.weekly_trends_pre || {};
  // 舊掃描結果沒有 weekly_trends_pre，此時只能顯示帳面 P/L
  const hasPreTrend = Object.keys(trendsPre).length > 0;
  const allWeeks = data?.all_weeks || [];
  const summary = data?.summary || {};
  const hasData = !!data;

  return (
    <div className="content">
      {/* ⚠️ 版面順序（Brad 2026-09-10 指定）：
             標題（右側掛計分規則）→ 預覽掃描 → Summary → tab → 表格。
          🔑 前三塊都**不隨 tab 改變**：預覽掃描是上傳控制項，四個 Summary 是
             整份掃描的統計（風險分級計數與集團總數）；三個 tab 只是換看
             同一份結果的哪一張表。所以它們排在 tab 上面是正確的 ——
             反過來（像先前把 Summary 放在 tab 底下）會讀成「這是當前分頁的數字」。
          ⚠️ 計分規則是 modal，屬於**動作**不是範圍控制 → 走 header 的
             `actions` 槽，不是 `scope`。 */}
      <TinyPageHeader
        title="EcoBot Killer 偵測"
        description={
          hasData
            ? `資料範圍 ${data.weeks_range}　掃描時間 ${toUTC8(data.scan_date)}（UTC+8）`
            : "找出可能在利用 EcoBot 機制獲利的玩家與集團，依四項指標評分。"
        }
        actions={
          <Button
            color="link"
            onClick={() => setRulesOpen(true)}
            style={{ color: '#9A9A9A', fontSize: '0.88em', padding: '4px 0', textDecoration: 'none' }}
          >
            <i className="fa fa-circle-question" style={{ marginRight: 6 }} />計分規則
          </Button>
        }
      />

      {/* ── Preview Scan Banner (when showing preview results) ── */}
      {isPreviewScan && (
        <Alert color="warning" className="mt-2" style={{ background: "rgba(255,165,0,0.12)", border: "1px solid rgba(255,165,0,0.3)", color: "#ffa500" }}>
          <i className="fa fa-eye" style={{ marginRight: 8 }} />
          <strong>目前顯示的是預覽掃描結果</strong>
          <span style={{ marginLeft: 8, color: "#ccc" }}>正式結果將於每週報表流程後自動更新</span>
        </Alert>
      )}

      {/* ── Preview Scan Section ── */}
      <Card className="mt-2" style={{ border: "1px solid rgba(255,255,255,0.08)" }}>
        <CardHeader
          style={{ cursor: "pointer", padding: "12px 20px" }}
          onClick={() => !isPreviewRunning && setPreviewOpen(!previewOpen)}
        >
          <Row className="align-items-center">
            <Col>
              <span style={{ color: "#fff", fontWeight: 500 }}>
                <i className="fa fa-upload" style={{ marginRight: 8, color: "#1f8ef1" }} />
                預覽掃描
                {isPreviewRunning && <TinyBusy style={{ marginLeft: 8 }} />}
              </span>
            </Col>
            <Col md="auto">
              {!isPreviewRunning && (
                <i className={`fa fa-chevron-${previewOpen ? 'up' : 'down'}`} style={{ color: "#9A9A9A" }} />
              )}
            </Col>
          </Row>

          {/* Progress bar (always visible when running) */}
          {isPreviewRunning && (
            <div style={{ marginTop: 12 }}>
              <Progress
                value={previewStepInfo.pct || 0}
                color="info"
                style={{ height: 8, background: "rgba(255,255,255,0.1)" }}
              />
              <p style={{ color: "#9A9A9A", fontSize: "0.82em", margin: "6px 0 0 0" }}>
                {previewJob.message || previewStepInfo.label}
                <span style={{ float: "right", color: "#666" }}>
                  重新整理頁面不影響掃描進度
                </span>
              </p>
            </div>
          )}

          {/* Completed/failed summary (only when current data is from preview) */}
          {previewJob && previewJob.status === 'completed' && !previewOpen && showPreviewJobMsg && (
            <p style={{ color: "#00d6b4", fontSize: "0.82em", margin: "8px 0 0 0" }}>
              <i className="fa fa-check" style={{ marginRight: 4 }} />{previewJob.message}
            </p>
          )}
          {previewJob && previewJob.status === 'failed' && !previewOpen && showPreviewJobMsg && (
            <p style={{ color: "#fd5d93", fontSize: "0.82em", margin: "8px 0 0 0" }}>
              <i className="fa fa-times" style={{ marginRight: 4 }} />{previewJob.message}
            </p>
          )}
        </CardHeader>

        <Collapse isOpen={previewOpen && !isPreviewRunning}>
          <CardBody style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
            <Row>
              <Col md="4">
                <FormGroup>
                  <label style={{ color: "#9A9A9A", fontSize: "0.85em", marginBottom: 6, display: "block" }}>日期範圍</label>
                  <Input
                    type="text"
                    placeholder="YYYYMMDD-YYYYMMDD"
                    value={previewWeek}
                    onChange={e => setPreviewWeek(e.target.value.trim())}
                    style={inputStyle}
                    disabled={previewUploading}
                  />
                  <small style={{ color: "#666" }}>例：20260223-20260227</small>
                </FormGroup>
              </Col>
              <Col md="5">
                <FormGroup>
                  <label style={{ color: "#9A9A9A", fontSize: "0.85em", marginBottom: 6, display: "block" }}>上傳 Excel 檔案</label>
                  <input
                    type="file"
                    accept=".xlsx"
                    multiple
                    ref={fileInputRef}
                    onChange={e => setPreviewFiles(Array.from(e.target.files || []))}
                    style={{ display: "none" }}
                    disabled={previewUploading}
                  />
                  <div
                    onClick={() => !previewUploading && fileInputRef.current?.click()}
                    onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = "#1f8ef1"; }}
                    onDragLeave={e => { e.preventDefault(); e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)"; }}
                    onDrop={e => {
                      e.preventDefault();
                      e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
                      if (previewUploading) return;
                      const dropped = Array.from(e.dataTransfer.files || []).filter(f => f.name.endsWith('.xlsx'));
                      if (dropped.length > 0) setPreviewFiles(dropped);
                    }}
                    style={{
                      border: "2px dashed rgba(255,255,255,0.15)",
                      borderRadius: 8,
                      padding: previewFiles.length > 0 ? "12px 16px" : "20px 16px",
                      textAlign: "center",
                      cursor: previewUploading ? "not-allowed" : "pointer",
                      transition: "border-color 0.2s, background 0.2s",
                      background: "rgba(255,255,255,0.02)",
                      opacity: previewUploading ? 0.5 : 1,
                    }}
                    onMouseEnter={e => { if (!previewUploading) e.currentTarget.style.borderColor = "#1f8ef1"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)"; }}
                  >
                    {previewFiles.length === 0 ? (
                      <>
                        <i className="fa fa-cloud-upload" style={{ fontSize: "1.6rem", color: "#1f8ef1", display: "block", marginBottom: 6 }} />
                        <span style={{ color: "#9A9A9A", fontSize: "0.88em" }}>
                          點擊選擇或拖曳 <strong style={{ color: "#fff" }}>.xlsx</strong> 檔案至此
                        </span>
                      </>
                    ) : (
                      <div style={{ textAlign: "left" }}>
                        {previewFiles.map((f, i) => (
                          <div key={i} style={{ display: "flex", alignItems: "center", marginBottom: i < previewFiles.length - 1 ? 4 : 0 }}>
                            <i className="fa fa-file-excel" style={{ color: "#00d6b4", marginRight: 8, fontSize: "1.1em" }} />
                            <span style={{ color: "#fff", fontSize: "0.88em", flex: 1 }}>{f.name}</span>
                            <span style={{ color: "#666", fontSize: "0.78em" }}>{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                          </div>
                        ))}
                        <div style={{ marginTop: 6, borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 6 }}>
                          <small style={{ color: "#1f8ef1", cursor: "pointer" }} onClick={e => { e.stopPropagation(); setPreviewFiles([]); if (fileInputRef.current) fileInputRef.current.value = ""; }}>
                            <i className="fa fa-times" style={{ marginRight: 4 }} />清除已選檔案
                          </small>
                        </div>
                      </div>
                    )}
                  </div>
                </FormGroup>
              </Col>
              <Col md="3" className="d-flex align-items-end" style={{ paddingBottom: "1rem" }}>
                <Button
                  color="info"
                  onClick={handlePreviewScan}
                  disabled={previewUploading || !previewWeek || previewFiles.length === 0}
                  style={{ width: "100%", height: 44 }}
                >
                  {previewUploading ? (
                    <><TinyBusy light style={{ marginRight: 6 }} />上傳中</>
                  ) : (
                    <><i className="fa fa-play" style={{ marginRight: 6 }} />開始掃描</>
                  )}
                </Button>
              </Col>
            </Row>
            {previewError && (
              <Alert color="danger" style={{ marginTop: 8, padding: "8px 16px", fontSize: "0.88em" }}>
                {previewError}
              </Alert>
            )}
          </CardBody>
        </Collapse>
      </Card>

      {/* ── No data message ── */}
      {!hasData && (
        <Alert color="info" className="mt-3">
          尚無掃描結果。請使用上方的預覽掃描上傳資料，或等待每週報表流程自動執行。
        </Alert>
      )}

      {hasData && (
        <>
          {/* ⚠️ 四張整片實色漸層卡（2.2rem 數字，約 90px 高）是全站唯一一組
                 這種樣式，而且把主表推到摺線以下。Brad 2026-09-10：
                 「我需要一打開這一頁就看到主要的 table，summary 只是附屬的」
                 → 換成公版的 TinyStatStrip（約 57px），配色改成同色系淡染，
                    風險等級的顏色語意（紅／橘／藍）保留。
              ⚠️ Summary 在 tab **上面** —— 它是整份掃描的統計，不隨 tab 變。 */}
          <TinyStatStrip
            style={{ marginTop: 8, marginBottom: 12 }}
            items={[
              { label: 'HIGH RISK', icon: 'fa-triangle-exclamation', color: '245, 54, 92', iconColor: '#f5365c', value: summary.high || 0 },
              { label: 'MEDIUM RISK', icon: 'fa-circle-exclamation', color: '255, 165, 0', iconColor: '#ffa500', value: summary.medium || 0 },
              { label: 'LOW RISK', icon: 'fa-circle-info', color: '31, 142, 241', iconColor: '#1f8ef1', value: summary.low || 0 },
              { label: '可疑集團', icon: 'fa-users', color: '102, 126, 234', iconColor: '#667eea', value: groups.length },
            ]}
          />

          {/* tab 在 Summary 底下：它換的是「看哪一張表」，不影響上面的統計 */}
          <TinyPageTabs
            items={EXPLOIT_TABS}
            active={activeTab}
            onChange={setActiveTab}
            style={{ marginBottom: 4 }}
          />

          <TabContent activeTab={activeTab} className="mt-0">
            {/* ═══════════ Tab 1: 風險名單 ═══════════ */}
            <TabPane tabId="risk-list">
              <Card>
                <CardHeader>
                  <Row className="align-items-center">
                    <Col>
                      <CardTitle tag="h4" style={{ color: "#fff", margin: 0 }}>
                        可疑玩家名單
                        {suspectsLoading && <TinyBusy style={{ marginLeft: 8 }} />}
                        <span style={{ color: '#9A9A9A', fontSize: '0.6em', fontWeight: 400, marginLeft: 12 }}>
                          共 {paged.total} 筆
                        </span>
                      </CardTitle>
                    </Col>
                  </Row>
                  {/* Filters */}
                  <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
                    {/* Risk Filter */}
                    <div>
                      <span style={{ color: '#9A9A9A', fontSize: '0.78em', marginRight: 8 }}>風險等級:</span>
                      {['HIGH', 'MEDIUM', 'LOW'].map(risk => {
                        const active = filterRisk.includes(risk);
                        const colors = { HIGH: '#ff4444', MEDIUM: '#ffa500', LOW: '#1f8ef1' };
                        return (
                          <span
                            key={risk}
                            onClick={() => toggleRiskFilter(risk)}
                            style={{
                              display: 'inline-block', padding: '2px 10px', borderRadius: 4,
                              cursor: 'pointer', marginRight: 6, fontSize: '0.82em', transition: 'all 0.15s',
                              background: active ? colors[risk] + '33' : 'transparent',
                              color: active ? colors[risk] : '#666',
                              border: `1px solid ${active ? colors[risk] + '66' : 'rgba(255,255,255,0.1)'}`,
                            }}
                          >
                            {risk} ({risk === 'HIGH' ? summary.high : risk === 'MEDIUM' ? summary.medium : summary.low})
                          </span>
                        );
                      })}
                    </div>
                    {/* Disposition Filter */}
                    <div>
                      <span style={{ color: '#9A9A9A', fontSize: '0.78em', marginRight: 8 }}>處置狀態:</span>
                      {DISPOSITION_OPTIONS.map(opt => {
                        const active = filterDisposition.includes(opt.value);
                        const c = opt.color || '#888';
                        return (
                          <span
                            key={opt.value}
                            onClick={() => toggleDispositionFilter(opt.value)}
                            style={{
                              display: 'inline-block', padding: '2px 10px', borderRadius: 4,
                              cursor: 'pointer', marginRight: 6, fontSize: '0.82em', transition: 'all 0.15s',
                              background: active ? c + '33' : 'transparent',
                              color: active ? c : '#666',
                              border: `1px solid ${active ? c + '66' : 'rgba(255,255,255,0.1)'}`,
                            }}
                          >
                            {opt.label}
                          </span>
                        );
                      })}
                    </div>
                    {/* Game Type Filter */}
                    {(data?.available_game_types || []).length > 0 && (
                      <div>
                        <span style={{ color: '#9A9A9A', fontSize: '0.78em', marginRight: 8 }}>遊戲類型:</span>
                        {(data.available_game_types || []).map(gt => {
                          const active = filterGameType.includes(gt);
                          const gtc = gameTypeColor(gt);
                          return (
                            <span
                              key={gt}
                              onClick={() => toggleGameTypeFilter(gt)}
                              style={{
                                display: 'inline-block', padding: '2px 10px', borderRadius: 4,
                                cursor: 'pointer', marginRight: 6, fontSize: '0.82em', transition: 'all 0.15s',
                                background: active ? gtc + '33' : 'transparent',
                                color: active ? gtc : '#666',
                                border: `1px solid ${active ? gtc + '66' : 'rgba(255,255,255,0.1)'}`,
                              }}
                            >
                              {gt}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {/* Stake Filter */}
                    {(data?.available_stakes || []).length > 0 && (
                      <div>
                        <span style={{ color: '#9A9A9A', fontSize: '0.78em', marginRight: 8 }}>級別 (BB):</span>
                        {(data.available_stakes || []).map(bb => {
                          const active = filterStake.includes(bb);
                          return (
                            <span
                              key={bb}
                              onClick={() => toggleStakeFilter(bb)}
                              style={{
                                display: 'inline-block', padding: '2px 10px', borderRadius: 4,
                                cursor: 'pointer', marginRight: 6, fontSize: '0.82em', transition: 'all 0.15s',
                                background: active ? '#2980b933' : 'transparent',
                                color: active ? '#3498db' : '#666',
                                border: `1px solid ${active ? '#2980b966' : 'rgba(255,255,255,0.1)'}`,
                              }}
                            >
                              {bb}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardBody>
                  <div style={{ overflowX: "auto" }}>
                    <Table className="tablesorter" responsive>
                      <thead className="text-primary">
                        <tr>
                          <th>風險</th>
                          <SortHeader field="score_total" className="text-center">分數</SortHeader>
                          <th>玩家 ID</th>
                          <th>暱稱</th>
                          <SortHeader field="club_id">俱樂部</SortHeader>
                          <SortHeader field="sa_id">超代</SortHeader>
                          <SortHeader field="agent_id">代理</SortHeader>
                          <SortHeader field="primary_game_type">主要遊戲</SortHeader>
                          <SortHeader field="total_pl" className="text-right">總 P/L</SortHeader>
                          <SortHeader field="eco_net" className="text-right" title="逐桌雙向分攤：EcoBot 輸的桌按贏家比例分攤(+)、贏的桌按輸家比例分攤(−)，相加即淨額">從EcoBot拿走</SortHeader>
                          <SortHeader field="eco_dep" className="text-center" title="從EcoBot拿走 ÷ 該玩家水前總獲利">EcoBot依賴度</SortHeader>
                          <th className="text-center">F1</th>
                          <th className="text-center">F2</th>
                          <th className="text-center">F3</th>
                          <th className="text-center">F4</th>
                          <SortHeader field="total_hands" className="text-right">手數</SortHeader>
                          <th className="text-center">處置</th>
                          <th className="text-center">備註</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paged.suspects.map(s => (
                          <tr
                            key={s.pid}
                            onClick={() => handleSelectPlayer(s)}
                            style={{ cursor: "pointer", transition: "background 0.15s" }}
                            onMouseEnter={e => e.currentTarget.style.background = "rgba(31,142,241,0.08)"}
                            onMouseLeave={e => e.currentTarget.style.background = ""}
                          >
                            <td>{riskBadge(s.risk)}</td>
                            <td className="text-center">{s.score_total}</td>
                            <td><code style={{ color: "#1f8ef1" }}>{s.pid}</code></td>
                            <td>{s.nick}</td>
                            <td style={{ fontSize: "0.85em" }}>{s.club_name}</td>
                            <td style={{ fontSize: "0.85em" }}>{s.sa_id !== '-' ? `${s.sa_nick}` : '-'}</td>
                            <td style={{ fontSize: "0.85em" }}>{s.agent_id !== '-' ? `${s.agent_nick}` : '-'}</td>
                            <td>
                              {(() => { const c = gameTypeColor(s.primary_game_type); return (
                                <Badge style={{ background: c + '22', color: c, border: `1px solid ${c}44`, fontSize: '0.78em', padding: '2px 6px' }}>
                                  {s.primary_game || '-'}
                                </Badge>
                              ); })()}
                            </td>
                            <td className="text-right" style={{ color: s.total_pl > 0 ? "#00d6b4" : "#fd5d93" }}>
                              {fmt(s.total_pl)}
                            </td>
                            <td className="text-right" style={{
                              color: s.eco_net >= 100000 ? "#ff4444" : s.eco_net >= 30000 ? "#ffa500" : "#fff",
                              fontWeight: s.eco_net >= 30000 ? 600 : 400,
                            }}>
                              {fmtNew(s.eco_net)}
                            </td>
                            <td className="text-center">
                              <span style={{
                                color: s.eco_dep >= 50 ? "#ff4444" : s.eco_dep >= 30 ? "#ffa500" : "#fff",
                                fontWeight: s.eco_dep >= 30 ? 600 : 400,
                              }}>
                                {pctNew(s.eco_dep)}
                              </span>
                            </td>
                            <td className="text-center">{s.score_f1}</td>
                            <td className="text-center">{s.score_f2}</td>
                            <td className="text-center">{s.score_f3}</td>
                            <td className="text-center">{s.score_f4}</td>
                            <td className="text-right">{fmt(s.total_hands)}</td>
                            <td className="text-center">{dispositionBadge(s.disposition)}</td>
                            <td className="text-center">
                              {s.memo ? <i className="fa fa-comment" style={{ color: "#ffa500" }} title={s.memo} /> : ""}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  {/* Pagination */}
                  {paged.total_pages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                      <span style={{ color: '#9A9A9A', fontSize: '0.82em' }}>
                        第 {paged.page} / {paged.total_pages} 頁
                      </span>
                      <Pagination size="sm" style={{ margin: 0 }}>
                        <PaginationItem disabled={paged.page <= 1}>
                          <PaginationLink first onClick={() => handlePageChange(1)} />
                        </PaginationItem>
                        <PaginationItem disabled={paged.page <= 1}>
                          <PaginationLink previous onClick={() => handlePageChange(paged.page - 1)} />
                        </PaginationItem>
                        {Array.from({ length: Math.min(5, paged.total_pages) }, (_, i) => {
                          let p;
                          if (paged.total_pages <= 5) { p = i + 1; }
                          else if (paged.page <= 3) { p = i + 1; }
                          else if (paged.page >= paged.total_pages - 2) { p = paged.total_pages - 4 + i; }
                          else { p = paged.page - 2 + i; }
                          return (
                            <PaginationItem key={p} active={p === paged.page}>
                              <PaginationLink onClick={() => handlePageChange(p)}>{p}</PaginationLink>
                            </PaginationItem>
                          );
                        })}
                        <PaginationItem disabled={paged.page >= paged.total_pages}>
                          <PaginationLink next onClick={() => handlePageChange(paged.page + 1)} />
                        </PaginationItem>
                        <PaginationItem disabled={paged.page >= paged.total_pages}>
                          <PaginationLink last onClick={() => handlePageChange(paged.total_pages)} />
                        </PaginationItem>
                      </Pagination>
                    </div>
                  )}
                </CardBody>
              </Card>
            </TabPane>

            {/* ═══════════ Tab 2: 集團分析 ═══════════ */}
            <TabPane tabId="groups">
              {groups.map((g, gi) => {
                const groupSuspects = suspects.filter(s => g.player_pids?.includes(s.pid));
                if (groupSuspects.length === 0) return null;
                return (
                  <Card key={gi} className="mb-3">
                    <CardHeader>
                      <Row className="align-items-center">
                        <Col>
                          <CardTitle tag="h4" style={{ color: "#fff", margin: 0 }}>
                            <i className="fa fa-users" style={{ marginRight: 8, color: "#ffa500" }} />
                            {g.club_name} ({g.club_id})
                            {' '}
                            <span style={{
                              fontSize: '0.55em',
                              padding: '2px 8px',
                              borderRadius: 4,
                              background: g.group_level === 'agent' ? '#e14eca33' : g.group_level === 'sa' ? '#1f8ef133' : '#ffa50033',
                              color: g.group_level === 'agent' ? '#e14eca' : g.group_level === 'sa' ? '#1f8ef1' : '#ffa500',
                              verticalAlign: 'middle',
                            }}>
                              {g.group_level === 'agent' ? '同代理' : g.group_level === 'sa' ? '同超代' : '同俱樂部'}
                            </span>
                          </CardTitle>
                          <p style={{ color: "#9A9A9A", fontSize: "0.85em", margin: "4px 0 0 0" }}>
                            {g.sa_id !== '-' && <>超代: <strong style={{ color: "#1f8ef1" }}>{g.sa_id}</strong> ({g.sa_nick})</>}
                            {g.agent_id && g.agent_id !== '-' && <> | 代理: {g.agent_id}</>}
                            {(!g.agent_id || g.agent_id === '-') && g.agents?.length > 0 && <> | 代理: {g.agents.join(', ')}</>}
                          </p>
                        </Col>
                        <Col md="auto" className="text-right">
                          <div style={{ textAlign: "right" }}>
                            <span style={{ color: "#9A9A9A", fontSize: "0.8em" }}>贏錢帳號</span>
                            <h3 style={{ color: "#00d6b4", margin: 0 }}>{g.winning_player_count}</h3>
                          </div>
                        </Col>
                        <Col md="auto" className="text-right">
                          <div style={{ textAlign: "right" }}>
                            <span style={{ color: "#9A9A9A", fontSize: "0.8em" }}>集團合計 P/L</span>
                            <h3 style={{ color: "#00d6b4", margin: 0 }}>{fmt(g.total_pl)}</h3>
                          </div>
                        </Col>
                        <Col md="auto" className="text-right">
                          <div style={{ textAlign: "right" }}>
                            <span style={{ color: "#9A9A9A", fontSize: "0.8em" }}>共桌比例</span>
                            <h3 style={{ color: g.coplay_ratio > 0.3 ? "#ff4444" : "#fff", margin: 0 }}>
                              {pct(g.coplay_ratio * 100)}
                            </h3>
                          </div>
                        </Col>
                      </Row>
                    </CardHeader>
                    <CardBody>
                      <Table className="tablesorter" responsive>
                        <thead className="text-primary">
                          <tr>
                            <th>風險</th>
                            <th>分數</th>
                            <th>玩家 ID</th>
                            <th>暱稱</th>
                            <th>代理</th>
                            <th className="text-right">P/L</th>
                            <th className="text-right">從EcoBot拿走</th>
                            <th className="text-center">EcoBot依賴度</th>
                            <th className="text-right">手數</th>
                            <th className="text-center">週數</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupSuspects.sort((a, b) => b.total_pl - a.total_pl).map(s => (
                            <tr key={s.pid} onClick={() => handleSelectPlayer(s)} style={{ cursor: "pointer" }}>
                              <td>{riskBadge(s.risk)}</td>
                              <td className="text-center">{s.score_total}</td>
                              <td><code style={{ color: "#1f8ef1" }}>{s.pid}</code></td>
                              <td>{s.nick}</td>
                              <td style={{ fontSize: "0.85em" }}>{s.agent_id !== '-' ? `${s.agent_nick} (${s.agent_id})` : '-'}</td>
                              <td className="text-right" style={{ color: s.total_pl > 0 ? "#00d6b4" : "#fd5d93" }}>
                                {fmt(s.total_pl)}
                              </td>
                              <td className="text-right" style={{
                                color: s.eco_net >= 100000 ? "#ff4444" : s.eco_net >= 30000 ? "#ffa500" : "#fff",
                                fontWeight: s.eco_net >= 30000 ? 600 : 400,
                              }}>
                                {fmtNew(s.eco_net)}
                              </td>
                              <td className="text-center" style={{
                                color: s.eco_dep >= 50 ? "#ff4444" : s.eco_dep >= 30 ? "#ffa500" : "#fff",
                                fontWeight: s.eco_dep >= 30 ? 600 : 400,
                              }}>
                                {pctNew(s.eco_dep)}
                              </td>
                              <td className="text-right">{fmt(s.total_hands)}</td>
                              <td className="text-center">{s.weeks_count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </CardBody>
                  </Card>
                );
              })}
            </TabPane>

            {/* ═══════════ Tab 3: 個人明細 ═══════════ */}
            <TabPane tabId="detail">
              {!selectedPlayer ? (
                <Card>
                  <CardBody className="text-center py-5">
                    <i className="fa fa-hand-pointer" style={{ fontSize: "2rem", color: "#9A9A9A" }} />
                    <p style={{ color: "#9A9A9A", marginTop: 12 }}>請從風險名單或集團分析中點擊一位玩家查看詳情</p>
                  </CardBody>
                </Card>
              ) : (
                <Card>
                  <CardHeader>
                    <Row className="align-items-center">
                      <Col>
                        <CardTitle tag="h4" style={{ color: "#fff" }}>
                          {riskBadge(selectedPlayer.risk)}{" "}
                          <span style={{ marginLeft: 8 }}>{selectedPlayer.nick}</span>
                          <code style={{ marginLeft: 12, color: "#1f8ef1", fontSize: "0.85em" }}>{selectedPlayer.pid}</code>
                        </CardTitle>
                      </Col>
                      <Col md="auto">
                        <h2 style={{ color: "#fff", margin: 0 }}>
                          {selectedPlayer.score_total}
                          <span style={{ fontSize: "0.5em", color: "#9A9A9A" }}> / 100</span>
                        </h2>
                      </Col>
                    </Row>
                  </CardHeader>
                  <CardBody>
                    <Row>
                      <Col md="6">
                        <h5 style={{ color: "#1f8ef1", borderBottom: "1px solid rgba(255,255,255,0.1)", paddingBottom: 8, marginBottom: 16 }}>
                          <i className="fa fa-id-card" style={{ marginRight: 8 }} />帳號資訊
                        </h5>
                        <table style={{ width: "100%", color: "#fff" }}>
                          <tbody>
                            {[
                              ["俱樂部", `${selectedPlayer.club_name} (${selectedPlayer.club_id})`],
                              ["超代", selectedPlayer.sa_id !== '-' ? `${selectedPlayer.sa_nick} (${selectedPlayer.sa_id})` : '-'],
                              ["代理", selectedPlayer.agent_id !== '-' ? `${selectedPlayer.agent_nick} (${selectedPlayer.agent_id})` : '-'],
                              ["總 P/L", fmt(selectedPlayer.total_pl)],
                              ["總 Rake", fmt(selectedPlayer.total_rake)],
                              ["手數", fmt(selectedPlayer.total_hands)],
                              ["場數", selectedPlayer.tables_played],
                              ["活躍週數", selectedPlayer.weeks_count],
                              ["勝場率", pct(selectedPlayer.win_session_pct)],
                              ["加權 BB/100", selectedPlayer.weighted_bb_100],
                            ].map(([label, value], i) => (
                              <tr key={i} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                                <td style={{ padding: "6px 0", color: "#9A9A9A", width: "40%" }}>{label}</td>
                                <td style={{ padding: "6px 0" }}>{value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </Col>
                      <Col md="6">
                        <h5 style={{ color: "#ff4444", borderBottom: "1px solid rgba(255,255,255,0.1)", paddingBottom: 8, marginBottom: 16 }}>
                          <i className="fa fa-exclamation-triangle" style={{ marginRight: 8 }} />風險評分拆解
                        </h5>
                        {[
                          { label: "F1: EcoBot 依賴度", score: selectedPlayer.score_f1, max: 30, color: "#ff4444",
                            desc: `獲利中有 ${pctNew(selectedPlayer.eco_dep)} 來自 EcoBot` },
                          { label: "F2: 從 EcoBot 拿走金額", score: selectedPlayer.score_f2, max: 25, color: "#ffa500",
                            desc: `淨拿走: ${fmtNew(selectedPlayer.eco_net)}` },
                          { label: "F3: 集團協調", score: selectedPlayer.score_f3, max: 30, color: "#1f8ef1",
                            desc: selectedPlayer.score_f3 > 0
                              ? `偵測層級: ${selectedPlayer.score_f3_level === 'agent' ? '同代理' : selectedPlayer.score_f3_level === 'sa' ? '同超代' : '同俱樂部'}`
                              : "未偵測到集團行為" },
                          { label: "F4: 行為一致性", score: selectedPlayer.score_f4, max: 15, color: "#00d6b4",
                            desc: `${selectedPlayer.weeks_count} 週活躍, ${fmt(selectedPlayer.total_hands)} 手` },
                        ].map((f, i) => (
                          <div key={i} style={{ marginBottom: 16 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                              <span style={{ color: "#fff", fontSize: "0.9em" }}>{f.label}</span>
                              <span style={{ color: f.color, fontWeight: 600 }}>{f.score}/{f.max}</span>
                            </div>
                            <div style={{ background: "rgba(255,255,255,0.1)", borderRadius: 4, height: 8, overflow: "hidden" }}>
                              <div style={{
                                width: `${(f.score / f.max) * 100}%`,
                                background: f.color,
                                height: "100%",
                                borderRadius: 4,
                                transition: "width 0.4s ease",
                              }} />
                            </div>
                            <p style={{ color: "#9A9A9A", fontSize: "0.78em", margin: "2px 0 0 0" }}>{f.desc}</p>
                          </div>
                        ))}

                        <div style={{ marginTop: 24, padding: 16, background: "rgba(255,68,68,0.08)", borderRadius: 8, border: "1px solid rgba(255,68,68,0.2)" }}>
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span style={{ color: "#9A9A9A" }} title="逐桌雙向分攤後的淨額">從 EcoBot 淨拿走</span>
                            <span style={{ color: "#ff4444", fontWeight: 600 }}>{fmtNew(selectedPlayer.eco_net)}</span>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                            <span style={{ color: "#9A9A9A" }}>EcoBot 依賴度</span>
                            <span style={{ color: "#ff4444" }}>{pctNew(selectedPlayer.eco_dep)}</span>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                            <span style={{ color: "#9A9A9A" }}>與 EcoBot 同桌數</span>
                            <span style={{ color: "#fff" }}>{selectedPlayer.eco_tables === undefined ? "—" : `${selectedPlayer.eco_tables} / ${selectedPlayer.tables_played}`}</span>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                            <span style={{ color: "#9A9A9A" }}>水前輸贏（扣 JP）</span>
                            <span style={{ color: selectedPlayer.total_pre > 0 ? "#00d6b4" : "#fd5d93" }}>{fmtNew(selectedPlayer.total_pre)}</span>
                          </div>
                          <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px solid rgba(255,255,255,0.08)", fontSize: "0.75em", color: "#9A9A9A" }}>
                            舊單向指標（僅供對照）：EcoBot 輸錢桌 P/L {fmt(selectedPlayer.ecobot_loss_tbl_pl)}、佔比 {pct(selectedPlayer.ecobot_loss_tbl_pct)}
                          </div>
                        </div>
                      </Col>
                    </Row>

                    {/* Game/Stake Breakdown */}
                    {selectedPlayer.game_stake_breakdown && selectedPlayer.game_stake_breakdown.length > 0 && (
                      <div style={{ marginTop: 24 }}>
                        <h5 style={{ color: "#bb6bd9", borderBottom: "1px solid rgba(255,255,255,0.1)", paddingBottom: 8, marginBottom: 16 }}>
                          <i className="fa fa-gamepad" style={{ marginRight: 8 }} />遊戲級別分析
                        </h5>
                        <Table className="tablesorter" responsive size="sm">
                          <thead className="text-primary">
                            <tr>
                              <th>遊戲</th>
                              <th>級別</th>
                              <th className="text-right">場數</th>
                              <th className="text-right">手數</th>
                              <th className="text-right">P/L</th>
                              <th className="text-right">Rake</th>
                              <th className="text-right">從EcoBot拿走</th>
                              <th className="text-right">Eco同桌數</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedPlayer.game_stake_breakdown.map((gs, i) => {
                              const isTop = i === 0 && gs.eco_net > 0;
                              const gtc = gameTypeColor(gs.game_type);
                              return (
                                <tr key={`${gs.game_type}-${gs.bb}`} style={isTop ? { background: gtc + '0D' } : {}}>
                                  <td>
                                    <Badge style={{ background: gtc + '22', color: gtc, border: `1px solid ${gtc}44`, fontSize: '0.82em', padding: '2px 8px' }}>
                                      {gs.game_type}
                                    </Badge>
                                    {isTop && <i className="fa fa-star" style={{ color: '#ffa500', marginLeft: 6, fontSize: '0.75em' }} title="主要狙擊遊戲" />}
                                  </td>
                                  <td style={{ fontWeight: 500 }}>{gs.stake_label}</td>
                                  <td className="text-right">{gs.tables}</td>
                                  <td className="text-right">{fmt(gs.hands)}</td>
                                  <td className="text-right" style={{ color: gs.pl > 0 ? '#00d6b4' : gs.pl < 0 ? '#fd5d93' : '#fff' }}>
                                    {fmt(gs.pl)}
                                  </td>
                                  <td className="text-right">{fmt(gs.rake)}</td>
                                  <td className="text-right" style={{ color: gs.eco_net > 0 ? '#ff4444' : gs.eco_net < 0 ? '#00d6b4' : '#9A9A9A', fontWeight: gs.eco_net > 0 ? 600 : 400 }}>
                                    {fmtNew(gs.eco_net)}
                                  </td>
                                  <td className="text-right">
                                    {gs.eco_tables > 0
                                      ? <span style={{ color: '#fff' }}>{gs.eco_tables} / {gs.tables}</span>
                                      : <span style={{ color: '#9A9A9A' }}>0</span>
                                    }
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    )}

                    {/* Weekly Trend Chart */}
                    {trends[selectedPlayer.pid] && (() => {
                      const usePre = trendMode === 'pre' && hasPreTrend;
                      const playerTrend = (usePre ? trendsPre : trends)[selectedPlayer.pid] || {};
                      const weekValues = allWeeks.map(w => playerTrend[w] || 0);
                      const labels = allWeeks.map(w => {
                        const parts = w.split('-');
                        return `${parts[0].slice(4, 6)}/${parts[0].slice(6)}`;
                      });

                      const chartData = {
                        labels,
                        datasets: [{
                          label: usePre ? '週水前輸贏(扣JP)' : '週帳面 P/L',
                          data: weekValues,
                          backgroundColor: weekValues.map(v => v >= 0 ? "rgba(0,214,180,0.7)" : "rgba(253,93,147,0.7)"),
                          borderColor: weekValues.map(v => v >= 0 ? "#00d6b4" : "#fd5d93"),
                          borderWidth: 1,
                          borderRadius: 3,
                          barPercentage: 0.7,
                        }],
                      };

                      const chartOptions = {
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { display: false },
                          tooltip: {
                            backgroundColor: 'rgba(0,0,0,0.85)',
                            padding: 10,
                            titleFont: { size: 12 },
                            bodyFont: { size: 12 },
                            callbacks: {
                              title: (items) => allWeeks[items[0].dataIndex] || '',
                              label: (ctx) => `${usePre ? '水前輸贏(扣JP)' : '帳面 P/L'}: ${Number(ctx.parsed.y).toLocaleString()}`,
                            },
                          },
                        },
                        scales: {
                          x: {
                            ticks: { color: '#9A9A9A', font: { size: 10 } },
                            grid: { display: false },
                          },
                          y: {
                            ticks: {
                              color: '#9A9A9A',
                              font: { size: 10 },
                              callback: (v) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v <= -1000 ? `${(v / 1000).toFixed(0)}K` : v,
                            },
                            grid: { color: 'rgba(255,255,255,0.06)', lineWidth: 0.5 },
                          },
                        },
                      };

                      const activeWeeks = weekValues.filter(v => v !== 0);
                      const totalWin = weekValues.filter(v => v > 0).reduce((a, b) => a + b, 0);
                      const totalLoss = weekValues.filter(v => v < 0).reduce((a, b) => a + b, 0);
                      const winWeeks = weekValues.filter(v => v > 0).length;

                      return (
                        <div style={{ marginTop: 24 }}>
                          <div style={{ display: "flex", alignItems: "center",
                                        borderBottom: "1px solid rgba(255,255,255,0.1)", paddingBottom: 8, flexWrap: "wrap", gap: 12 }}>
                            <h5 style={{ color: "#ffa500", margin: 0 }}>
                              <i className="fa fa-bar-chart" style={{ marginRight: 8 }} />每週輸贏趨勢
                            </h5>
                            {/* 水前 / 水後 切換（緊接標題後） */}
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <div style={{ display: "inline-flex", borderRadius: 6, overflow: "hidden",
                                            border: "1px solid rgba(255,255,255,0.15)" }}>
                                {[
                                  { key: "pre", label: "水前", tip: "牌桌真實輸贏 = Win + Rake + JP fee − JP payout（剝掉抽水與 JP 影響）" },
                                  { key: "net", label: "水後", tip: "帳面 P/L（Win）＝玩家錢包實際變化，已扣掉自己付的水" },
                                ].map(opt => {
                                  const active = (trendMode === opt.key) && (opt.key !== "pre" || hasPreTrend);
                                  const disabled = opt.key === "pre" && !hasPreTrend;
                                  return (
                                    <button
                                      key={opt.key}
                                      title={disabled ? "此筆掃描結果為舊版，無水前資料，請重新掃描" : opt.tip}
                                      disabled={disabled}
                                      onClick={() => setTrendMode(opt.key)}
                                      style={{
                                        background: active ? "#1f8ef1" : "transparent",
                                        color: disabled ? "#5a5a6a" : (active ? "#fff" : "#9A9A9A"),
                                        border: "none", padding: "3px 14px", fontSize: "0.82em",
                                        cursor: disabled ? "not-allowed" : "pointer", fontWeight: active ? 600 : 400,
                                      }}
                                    >{opt.label}</button>
                                  );
                                })}
                              </div>
                              <span style={{ color: "#9A9A9A", fontSize: "0.78em" }}>
                                {usePre
                                  ? "＝ Win + Rake + JP Fee − JP Payout"
                                  : "＝ Win（純 P/L，已扣水）"}
                              </span>
                            </div>
                          </div>
                          <div style={{ height: 220, marginTop: 12 }}>
                            <Bar data={chartData} options={chartOptions} />
                          </div>
                          <Row className="mt-3">
                            {[
                              { label: "活躍週數", value: `${activeWeeks.length} / ${allWeeks.length}`, color: "#fff" },
                              { label: "贏錢週數", value: `${winWeeks} 週`, color: "#00d6b4" },
                              { label: "總贏", value: fmt(totalWin), color: "#00d6b4" },
                              { label: "總輸", value: fmt(totalLoss), color: "#fd5d93" },
                              (() => {
                                const net = usePre
                                  ? (selectedPlayer.total_pre !== undefined ? selectedPlayer.total_pre : weekValues.reduce((a, b) => a + b, 0))
                                  : selectedPlayer.total_pl;
                                return { label: usePre ? "水前淨利" : "帳面淨利", value: fmt(net), color: net >= 0 ? "#00d6b4" : "#fd5d93" };
                              })(),
                            ].map((item, i) => (
                              <Col key={i} style={{ textAlign: "center", padding: "8px 4px" }}>
                                <div style={{ color: "#9A9A9A", fontSize: "0.75em", marginBottom: 2 }}>{item.label}</div>
                                <div style={{ color: item.color, fontWeight: 600, fontSize: "0.95em" }}>{item.value}</div>
                              </Col>
                            ))}
                          </Row>
                        </div>
                      );
                    })()}

                    {/* Disposition + Memo */}
                    <Row style={{ marginTop: 24 }}>
                      <Col md="6">
                        <h5 style={{ color: "#ffa500", borderBottom: "1px solid rgba(255,255,255,0.1)", paddingBottom: 8 }}>
                          <i className="fa fa-gavel" style={{ marginRight: 8 }} />處置方式
                        </h5>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                          {DISPOSITION_OPTIONS.map(opt => {
                            const active = dispositionValue === opt.value;
                            const c = opt.color || '#888';
                            return (
                              <div
                                key={opt.value}
                                onClick={() => !dispSaving && handleSaveDisposition(opt.value)}
                                style={{
                                  padding: '8px 16px', borderRadius: 6, cursor: dispSaving ? 'wait' : 'pointer',
                                  background: active ? c + '22' : 'rgba(255,255,255,0.03)',
                                  border: `1.5px solid ${active ? c : 'rgba(255,255,255,0.1)'}`,
                                  color: active ? c : '#9A9A9A',
                                  fontWeight: active ? 600 : 400, fontSize: '0.88em',
                                  transition: 'all 0.15s',
                                }}
                              >
                                {active && <i className="fa fa-check" style={{ marginRight: 6 }} />}
                                {opt.label}
                              </div>
                            );
                          })}
                        </div>
                        {dispSaving && <TinyBusy text="儲存中" />}
                      </Col>
                      <Col md="6">
                        <h5 style={{ color: "#9A9A9A", borderBottom: "1px solid rgba(255,255,255,0.1)", paddingBottom: 8 }}>
                          <i className="fa fa-pencil" style={{ marginRight: 8 }} />GM 備註
                        </h5>
                        <FormGroup>
                          <Input
                            type="textarea"
                            value={memoText}
                            onChange={e => setMemoText(e.target.value)}
                            placeholder="在此輸入備註..."
                            rows={3}
                            style={{ ...inputStyle, resize: "vertical" }}
                          />
                        </FormGroup>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={handleSaveMemo}
                          disabled={memoSaving}
                        >
                          {memoSaving ? <TinyBusy light /> : <><i className="fa fa-save" style={{ marginRight: 4 }} />儲存備註</>}
                        </Button>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              )}
            </TabPane>
          </TabContent>
        </>
      )}

      {/* ── Scoring Rules Modal ── */}
      {paged && paged.suspects && paged.suspects.length > 0 && isLegacy(paged.suspects[0]) && (
        <div style={{
          margin: '0 0 16px', padding: '12px 16px', borderRadius: 8,
          background: 'rgba(255,165,0,0.10)', border: '1px solid rgba(255,165,0,0.3)', color: '#ffa500',
        }}>
          <i className="fa fa-exclamation-triangle" style={{ marginRight: 8 }} />
          目前顯示的是 <strong>v2.24.0 之前的舊版掃描結果</strong>，EcoBot 相關欄位（從EcoBot拿走 / 依賴度）顯示為「—」。
          舊版指標為單向毛額、會嚴重高估依賴度。請執行一次週報 pipeline（Step 5）或預覽掃描以更新為雙向分攤結果。
        </div>
      )}

      <Modal isOpen={rulesOpen} toggle={() => setRulesOpen(false)} size="lg" style={{ maxWidth: 780 }}>
        <ModalHeader
          toggle={() => setRulesOpen(false)}
          style={{ background: '#1e1e2f', borderBottom: '1px solid rgba(255,255,255,0.08)', color: '#fff' }}
        >
          <i className="fa fa-calculator" style={{ marginRight: 8 }} />
          可疑玩家計分規則
        </ModalHeader>
        <ModalBody style={{ background: '#27293d', color: '#e0e0e0', fontSize: '0.92em', lineHeight: 1.7, maxHeight: '80vh', overflowY: 'auto' }}>
          {/* 設計理念 */}
          <div style={{ marginBottom: 24, padding: '14px 16px', background: 'rgba(31,142,241,0.06)', borderRadius: 8, border: '1px solid rgba(31,142,241,0.15)' }}>
            <h6 style={{ color: '#1f8ef1', fontWeight: 600, margin: '0 0 8px' }}>
              <i className="fa fa-lightbulb" style={{ marginRight: 8 }} />偵測設計理念
            </h6>
            <p style={{ color: '#ccc', margin: '0 0 8px' }}>
              單純贏錢不代表作弊，技術好的玩家本來就會贏。本系統的核心問題是：<strong style={{ color: '#fff' }}>「這個人的獲利是來自正常對局，還是專門針對 EcoBot 的弱點？」</strong>
            </p>
            <p style={{ color: '#ccc', margin: '0 0 8px' }}>
              為此，系統從四個不同維度交叉驗證，每個維度回答一個關鍵問題：
            </p>
            <div style={{ color: '#aaa', fontSize: '0.88em' }}>
              <div style={{ marginBottom: 3 }}><strong style={{ color: '#ff4444' }}>F1</strong> — 他的獲利有多少比例是從 EcoBot 身上拿的？（<em>依賴度</em>）</div>
              <div style={{ marginBottom: 3 }}><strong style={{ color: '#ffa500' }}>F2</strong> — 從 EcoBot 拿走多少錢？值不值得花資源調查？（<em>嚴重性</em>）</div>
              <div style={{ marginBottom: 3 }}><strong style={{ color: '#1f8ef1' }}>F3</strong> — 是不是有多個帳號在協同作業？（<em>組織性</em>）</div>
              <div><strong style={{ color: '#00d6b4' }}>F4</strong> — 是長期穩定的行為，還是偶爾運氣好？（<em>持續性</em>）</div>
            </div>
            <p style={{ color: '#999', margin: '10px 0 0', fontSize: '0.85em' }}>
              只有當多個維度同時亮紅燈，總分才會達到 HIGH。單一因子高分不代表一定在作弊，而是提供 GM 調查的優先順序。
            </p>
          </div>

          {/* 前置條件 */}
          <div style={{ marginBottom: 22 }}>
            <h6 style={{ color: '#1f8ef1', fontWeight: 600, marginBottom: 8 }}>
              <i className="fa fa-filter" style={{ marginRight: 8, fontSize: '0.9em' }} />前置條件
            </h6>
            <p style={{ color: '#999', margin: '0 0 6px', fontSize: '0.85em' }}>
              為避免把偶爾路過的普通玩家捲入，只有符合以下所有條件的玩家才會被納入評分：
            </p>
            <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 6, padding: '10px 14px' }}>
              <div>• 所有 Ring Game 遊戲類型（NLH、PLO、PLO5、PLO6、AFO、AFH 等）與所有級別皆納入掃描</div>
              <div>• 總手數 ≥ <strong style={{ color: '#fff' }}>200</strong>（太少手數可能只是短暫試玩）</div>
              <div>• 至少在 <strong style={{ color: '#fff' }}>5</strong> 張不同桌打過（排除只在一張桌坐過的人）</div>
              <div>• <strong style={{ color: '#fff' }}>從 EcoBot 淨拿走的錢 {'>'} 0</strong>（沒從 EcoBot 拿到錢就不是 exploit，而是一般贏家）</div>
              <div style={{ color: '#999', fontSize: '0.85em', marginTop: 4 }}>
                註：F3/F4 另需整體 P/L {'>'} 0；F1/F2 則不看整體輸贏 —— 有些人專打 EcoBot、輸給真人，整體是虧的，那才是最純粹的目標。
              </div>
            </div>
          </div>

          {/* 配分比例 */}
          <div style={{ marginBottom: 22 }}>
            <h6 style={{ color: '#fff', fontWeight: 600, marginBottom: 8 }}>
              <i className="fa fa-pie-chart" style={{ marginRight: 8, fontSize: '0.9em' }} />配分比例（總分 100）
            </h6>
            <div style={{ display: 'flex', gap: 0, borderRadius: 6, overflow: 'hidden', height: 28, marginBottom: 6 }}>
              <div style={{ width: '30%', background: '#ff4444', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.75em', fontWeight: 600 }}>F1: 30</div>
              <div style={{ width: '25%', background: '#ffa500', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.75em', fontWeight: 600 }}>F2: 25</div>
              <div style={{ width: '30%', background: '#1f8ef1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.75em', fontWeight: 600 }}>F3: 30</div>
              <div style={{ width: '15%', background: '#00d6b4', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.75em', fontWeight: 600 }}>F4: 15</div>
            </div>
            <p style={{ color: '#999', margin: 0, fontSize: '0.82em' }}>
              F1（關聯性）和 F3（組織性）合佔 60%，因為「跟 EcoBot 損失的關聯」和「是否有組織」是判斷狙擊的最核心指標。F2（損害金額）佔 25% 作為嚴重性權重。F4（持續性）佔 15% 作為輔助驗證。
            </p>
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.08)', margin: '20px 0' }} />

          {/* F1 */}
          <div style={{ marginBottom: 22 }}>
            <h6 style={{ color: '#ff4444', fontWeight: 600 }}>
              F1：EcoBot 依賴度（滿分 30）
            </h6>
            <p style={{ color: '#aaa', margin: '4px 0 6px' }}>
              <strong style={{ color: '#ddd' }}>核心邏輯：</strong>系統逐桌計算這個玩家與 EcoBot 之間的<strong style={{ color: '#fff' }}>淨金錢流動</strong>：
              EcoBot 該桌水前輸錢時，把它輸的金額按該桌真人贏家的獲利比例分攤過來（記正）；EcoBot 該桌水前贏錢時，
              把它贏的金額按真人輸家的虧損比例分攤回去（記負）。兩者相加，就是這個人真正從 EcoBot 身上拿走多少。
              依賴度 = 這個淨額 ÷ 他的水前總獲利。
            </p>
            <p style={{ color: '#999', margin: '0 0 8px', fontSize: '0.85em' }}>
              <strong style={{ color: '#ffa500' }}>v2.24.0 修正：</strong>舊版只累加「EcoBot 輸錢的桌」，不扣掉玩家在「EcoBot 贏錢的桌」輸回去的部分，
              是單向毛額、會嚴重高估（舊版有人算出 587% 甚至 114,276%）。改成雙向分攤後，全站分攤總額與 EcoBot 實際輸贏的回收率為 100.0%。
              依賴度超過 100% 仍代表他對真人玩家是輸的、獲利完全來自 EcoBot。
            </p>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <th style={{ padding: '6px 8px', color: '#9A9A9A', fontWeight: 500 }}>EcoBot 依賴度</th>
                <th style={{ padding: '6px 8px', color: '#9A9A9A', fontWeight: 500, textAlign: 'center', width: 80 }}>得分</th>
              </tr></thead>
              <tbody>
                {[['≥ 50%', 30, '過半獲利來自 EcoBot'], ['≥ 30%', 22, '相當比例來自 EcoBot'], ['≥ 15%', 14, '部分來自 EcoBot'], ['≥ 5%', 6, '少量'], ['< 5%', 0, '關聯不明顯']].map(([label, score, note]) => (
                  <tr key={label} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding: '5px 8px' }}>{label} <span style={{ color: '#666', fontSize: '0.82em' }}>— {note}</span></td>
                    <td style={{ padding: '5px 8px', textAlign: 'center', color: score > 0 ? '#ff4444' : '#666', fontWeight: score > 0 ? 600 : 400 }}>{score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* F2 */}
          <div style={{ marginBottom: 22 }}>
            <h6 style={{ color: '#ffa500', fontWeight: 600 }}>
              F2：從 EcoBot 拿走的金額（滿分 25）
            </h6>
            <p style={{ color: '#aaa', margin: '4px 0 6px' }}>
              <strong style={{ color: '#ddd' }}>核心邏輯：</strong>即使依賴度高，如果金額很小（例如只拿走幾百塊），可能不值得投入人力調查。
              這個因子讓 GM 能優先處理損害最大的案件，是一個「嚴重性優先級」的權重。
            </p>
            <p style={{ color: '#999', margin: '0 0 8px', fontSize: '0.85em' }}>
              <strong style={{ color: '#ffa500' }}>v2.24.0 修正：</strong>舊版吃的是玩家「總 P/L」，但那跟利用 EcoBot 沒有直接關係 ——
              一個靠贏真人賺錢的高手不該只因為贏很多就被評為 exploit 嫌疑。改成只看實際從 EcoBot 拿走的淨額。
            </p>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <th style={{ padding: '6px 8px', color: '#9A9A9A', fontWeight: 500 }}>從 EcoBot 淨拿走</th>
                <th style={{ padding: '6px 8px', color: '#9A9A9A', fontWeight: 500, textAlign: 'center', width: 80 }}>得分</th>
              </tr></thead>
              <tbody>
                {[['≥ 100,000', 25], ['≥ 30,000', 15], ['≥ 10,000', 5], ['< 10,000', 0]].map(([label, score]) => (
                  <tr key={label} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding: '5px 8px' }}>{label}</td>
                    <td style={{ padding: '5px 8px', textAlign: 'center', color: score > 0 ? '#ffa500' : '#666', fontWeight: score > 0 ? 600 : 400 }}>{score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* F3 */}
          <div style={{ marginBottom: 22 }}>
            <h6 style={{ color: '#1f8ef1', fontWeight: 600 }}>
              F3：集團協調（滿分 30）
            </h6>
            <p style={{ color: '#aaa', margin: '4px 0 6px' }}>
              <strong style={{ color: '#ddd' }}>核心邏輯：</strong>一個人贏錢可能是技術好，但如果同一個代理、超代、或俱樂部底下有多個帳號同時在贏 EcoBot 的錢，
              就不太可能是巧合了。這個因子偵測的是「有組織的狙擊集團」。
            </p>
            <p style={{ color: '#999', margin: '0 0 6px', fontSize: '0.85em' }}>
              系統會在三個層級分別偵測：<strong style={{ color: '#e14eca' }}>同代理</strong>（最緊密）、
              <strong style={{ color: '#1f8ef1' }}>同超代</strong>（中等）、<strong style={{ color: '#ffa500' }}>同俱樂部</strong>（最寬鬆），
              最終取三層中最高分。分組越緊密，代表串通可能性越高，計分也越重。
            </p>
            <p style={{ color: '#999', margin: '0 0 6px', fontSize: '0.85em' }}>
              若玩家無代理 ID 或無超代 ID（以 '-' 表示），則不會被納入該層的分組，避免誤判。
              共桌率使用 Jaccard 相似度計算：同組帳號共同出現的桌數 ÷ 各自桌子的聯集。
            </p>

            <p style={{ color: '#ddd', fontWeight: 600, marginTop: 12, marginBottom: 4, fontSize: '0.9em' }}>
              <span style={{ color: '#e14eca' }}>■</span> 同代理層（滿分 30，需 ≥ 2 人）
            </p>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 10 }}>
              <thead><tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <th style={{ padding: '5px 8px', color: '#9A9A9A', fontWeight: 500, fontSize: '0.85em' }}>贏錢帳號數</th>
                <th style={{ padding: '5px 8px', color: '#9A9A9A', fontWeight: 500, textAlign: 'center', fontSize: '0.85em' }}>共桌率</th>
                <th style={{ padding: '5px 8px', color: '#9A9A9A', fontWeight: 500, textAlign: 'center', width: 65, fontSize: '0.85em' }}>得分</th>
              </tr></thead>
              <tbody>
                {[['2 人', '< 10%', 20], ['2 人', '10–30%', 23], ['2 人', '> 30%', 26],
                  ['3+ 人', '< 10%', 24], ['3+ 人', '10–30%', 27], ['3+ 人', '> 30%', 30]
                ].map(([n, cp, score], i) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding: '4px 8px', fontSize: '0.85em' }}>{n}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'center', fontSize: '0.85em' }}>{cp}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'center', color: '#e14eca', fontWeight: 600, fontSize: '0.85em' }}>{score}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p style={{ color: '#ddd', fontWeight: 600, marginBottom: 4, fontSize: '0.9em' }}>
              <span style={{ color: '#1f8ef1' }}>■</span> 同超代層（滿分 25，需 ≥ 2 人）
            </p>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 10 }}>
              <thead><tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <th style={{ padding: '5px 8px', color: '#9A9A9A', fontWeight: 500, fontSize: '0.85em' }}>贏錢帳號數</th>
                <th style={{ padding: '5px 8px', color: '#9A9A9A', fontWeight: 500, textAlign: 'center', fontSize: '0.85em' }}>共桌率</th>
                <th style={{ padding: '5px 8px', color: '#9A9A9A', fontWeight: 500, textAlign: 'center', width: 65, fontSize: '0.85em' }}>得分</th>
              </tr></thead>
              <tbody>
                {[['2 人', '< 10%', 16], ['2 人', '10–30%', 19], ['2 人', '> 30%', 21],
                  ['3+ 人', '< 10%', 20], ['3+ 人', '10–30%', 23], ['3+ 人', '> 30%', 25]
                ].map(([n, cp, score], i) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding: '4px 8px', fontSize: '0.85em' }}>{n}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'center', fontSize: '0.85em' }}>{cp}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'center', color: '#1f8ef1', fontWeight: 600, fontSize: '0.85em' }}>{score}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p style={{ color: '#ddd', fontWeight: 600, marginBottom: 4, fontSize: '0.9em' }}>
              <span style={{ color: '#ffa500' }}>■</span> 同俱樂部層（滿分 20，需 ≥ 3 人）
            </p>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 10 }}>
              <thead><tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <th style={{ padding: '5px 8px', color: '#9A9A9A', fontWeight: 500, fontSize: '0.85em' }}>贏錢帳號數</th>
                <th style={{ padding: '5px 8px', color: '#9A9A9A', fontWeight: 500, textAlign: 'center', fontSize: '0.85em' }}>共桌率</th>
                <th style={{ padding: '5px 8px', color: '#9A9A9A', fontWeight: 500, textAlign: 'center', width: 65, fontSize: '0.85em' }}>得分</th>
              </tr></thead>
              <tbody>
                {[['3 人', '≤ 30%', 15], ['3 人', '> 30%', 19],
                  ['4+ 人', '≤ 30%', 16], ['4+ 人', '> 30%', 20]
                ].map(([n, cp, score], i) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding: '4px 8px', fontSize: '0.85em' }}>{n}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'center', fontSize: '0.85em' }}>{cp}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'center', color: '#ffa500', fontWeight: 600, fontSize: '0.85em' }}>{score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ color: '#888', fontSize: '0.8em', marginTop: 6 }}>
              ※ 玩家的最終 F3 分數 = 三層中的<strong>最高分</strong>。若無代理/超代 ID，則不納入該層計算。
            </p>
          </div>

          {/* F4 */}
          <div style={{ marginBottom: 22 }}>
            <h6 style={{ color: '#00d6b4', fontWeight: 600 }}>
              F4：行為一致性（滿分 15）
            </h6>
            <p style={{ color: '#aaa', margin: '4px 0 6px' }}>
              <strong style={{ color: '#ddd' }}>核心邏輯：</strong>運氣好的人可能某一週突然贏很多，但不太可能連續好幾週、上千手牌都持續贏。
              這個因子用來區分「偶發的運氣」和「系統性的行為」。三項條件獨立判定，符合幾項加幾項分。
            </p>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <th style={{ padding: '6px 8px', color: '#9A9A9A', fontWeight: 500 }}>條件</th>
                <th style={{ padding: '6px 8px', color: '#9A9A9A', fontWeight: 500, textAlign: 'center', width: 80 }}>得分</th>
              </tr></thead>
              <tbody>
                {[
                  ['勝場率 ≥ 45% 且至少打 10 場 — 穩定贏家，不是靠單場翻盤', '+5'],
                  ['活躍週數 ≥ 3 週 — 持續性行為，非偶發一次', '+5'],
                  ['總手數 ≥ 2,000 手 — 大量對局，樣本足夠排除運氣', '+5'],
                ].map(([cond, score], i) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding: '5px 8px' }}>{cond}</td>
                    <td style={{ padding: '5px 8px', textAlign: 'center', color: '#00d6b4', fontWeight: 600 }}>{score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.08)', margin: '20px 0' }} />

          {/* 風險等級 */}
          <div style={{ marginBottom: 22 }}>
            <h6 style={{ color: '#fff', fontWeight: 600, marginBottom: 8 }}>
              <i className="fa fa-flag" style={{ marginRight: 8, fontSize: '0.9em' }} />風險等級判定
            </h6>
            <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
              {[
                { label: 'HIGH', range: '70–100', color: '#ff4444', desc: '高度可疑，建議立即調查' },
                { label: 'MEDIUM', range: '40–69', color: '#ffa500', desc: '中度可疑，需持續關注' },
                { label: 'LOW', range: '20–39', color: '#1f8ef1', desc: '輕度可疑，納入觀察' },
              ].map(r => (
                <div key={r.label} style={{
                  flex: 1, padding: '10px 12px', borderRadius: 6,
                  background: r.color + '15', border: `1px solid ${r.color}33`, textAlign: 'center',
                }}>
                  <Badge style={{ background: r.color, fontSize: '0.85em', padding: '3px 10px' }}>{r.label}</Badge>
                  <div style={{ color: '#fff', fontWeight: 600, fontSize: '1.1em', margin: '6px 0 2px' }}>{r.range}</div>
                  <div style={{ color: '#aaa', fontSize: '0.78em' }}>{r.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* 範例 */}
          <div style={{ marginBottom: 16 }}>
            <h6 style={{ color: '#fff', fontWeight: 600, marginBottom: 10 }}>
              <i className="fa fa-user" style={{ marginRight: 8, fontSize: '0.9em' }} />評分範例
            </h6>
            <Row>
              <Col md="6">
                <div style={{ padding: '12px 14px', borderRadius: 6, background: 'rgba(255,68,68,0.06)', border: '1px solid rgba(255,68,68,0.15)', marginBottom: 8 }}>
                  <div style={{ fontWeight: 600, color: '#ff4444', marginBottom: 6 }}>範例 A：典型高風險狙擊者</div>
                  <div style={{ fontSize: '0.85em' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}><span style={{ color: '#aaa' }}>F1: EcoBot佔比 95%</span><span style={{ color: '#ff4444' }}>22</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}><span style={{ color: '#aaa' }}>F2: P/L = 80,000</span><span style={{ color: '#ffa500' }}>15</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}><span style={{ color: '#aaa' }}>F3: 同超代3人, 共桌25%</span><span style={{ color: '#1f8ef1' }}>27</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}><span style={{ color: '#aaa' }}>F4: 穩定+長期+大量</span><span style={{ color: '#00d6b4' }}>15</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0 0', borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: 4, fontWeight: 600 }}>
                      <span style={{ color: '#fff' }}>總分</span><span style={{ color: '#ff4444' }}>79 → HIGH</span>
                    </div>
                  </div>
                </div>
              </Col>
              <Col md="6">
                <div style={{ padding: '12px 14px', borderRadius: 6, background: 'rgba(31,142,241,0.06)', border: '1px solid rgba(31,142,241,0.15)', marginBottom: 8 }}>
                  <div style={{ fontWeight: 600, color: '#1f8ef1', marginBottom: 6 }}>範例 B：可能只是技術好的玩家</div>
                  <div style={{ fontSize: '0.85em' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}><span style={{ color: '#aaa' }}>F1: EcoBot佔比 45%</span><span style={{ color: '#ff4444' }}>6</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}><span style={{ color: '#aaa' }}>F2: P/L = 25,000</span><span style={{ color: '#ffa500' }}>5</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}><span style={{ color: '#aaa' }}>F3: 超代只他1人贏</span><span style={{ color: '#666' }}>0</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}><span style={{ color: '#aaa' }}>F4: 長期+大量</span><span style={{ color: '#00d6b4' }}>10</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0 0', borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: 4, fontWeight: 600 }}>
                      <span style={{ color: '#fff' }}>總分</span><span style={{ color: '#1f8ef1' }}>21 → LOW</span>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
            <p style={{ color: '#999', fontSize: '0.82em', margin: '4px 0 0' }}>
              範例 B 雖然有穩定贏錢的能力，但他的獲利來源不只是 EcoBot 輸桌（佔比只有 45%），且沒有集團行為，更可能是靠自身技術贏錢的正常玩家。
            </p>
          </div>

          {/* 重要提醒 */}
          <div style={{ padding: '12px 14px', borderRadius: 6, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <p style={{ color: '#ffa500', fontWeight: 600, margin: '0 0 6px', fontSize: '0.88em' }}>
              <i className="fa fa-exclamation-triangle" style={{ marginRight: 6 }} />重要提醒
            </p>
            <p style={{ color: '#999', margin: 0, fontSize: '0.85em' }}>
              分數只是提供調查的優先順序，不等於定罪。HIGH 分數的玩家也可能是正常的高手，LOW 分數的玩家也可能有其他可疑行為是系統未涵蓋的。
              最終判斷仍需 GM 結合遊戲級別分析、對局記錄、代理關係等資訊進行人工研判。
            </p>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
}

export default TinyExploitDetection;
